/**
 * Shared agent payload builder.
 * IMPORTANT:
 * - Incoming agents MUST mirror AgentCreationWizard logic exactly
 * - Flow agents preserve existing behavior
 * - Guided Wizard is NOT modified and remains the source of truth
 * - Voice providers: ElevenLabs (ConvAI), Bulbul (Sarvam AI), Nova Sonic (AWS), Gemini Live, Kie.ai ElevenLabs
 */

type VoiceProvider = "elevenlabs" | "bulbul" | "nova_sonic" | "gemini" | "kie-elevenlabs";

interface BuildAgentPayloadParams {
  formData: any;
  activeVoiceProvider: VoiceProvider;
  selectedPhoneNumber?: any;
  isEdit?: boolean;
}

export function buildAgentCreatePayload({
  formData,
  activeVoiceProvider,
  selectedPhoneNumber,
  isEdit = false,
}: BuildAgentPayloadParams) {
  const isIncoming = formData.type === "incoming";

  // ✅ FLOW / OUTBOUND AGENTS
  if (!isIncoming) {
    const flowPayload: any = {
      ...formData,
      voiceProvider: activeVoiceProvider,
    };

    // Map voice fields based on active provider
    if (activeVoiceProvider === "bulbul") {
      flowPayload.bulbulVoice = formData.bulbulVoice;
      flowPayload.bulbulLanguage = formData.bulbulLanguage || formData.language;
    } else if (activeVoiceProvider === "nova_sonic") {
      flowPayload.novaSonicVoice = formData.novaSonicVoice;
    } else if (activeVoiceProvider === "gemini") {
      flowPayload.geminiVoice = formData.geminiVoice;
    } else if (activeVoiceProvider === "kie-elevenlabs") {
      flowPayload.kieVoice = formData.kieVoice;
    }
    // For ElevenLabs, keep elevenLabsVoiceId as-is

    return flowPayload;
  }

  // ✅ INCOMING AGENTS — MIRROR AGENT CREATION WIZARD EXACTLY

  // Derive telephony provider (Wizard logic)
  const derivedTelephonyProvider =
    selectedPhoneNumber?.type ||
    "twilio";
  // Note: 'plivo' type is returned from unallocated-numbers for Indian numbers

  const payload: any = {
    name: formData.name,
    telephonyProvider: derivedTelephonyProvider,
    phoneNumberId: formData.phoneNumberId,
    language: formData.language,
    voiceTone: formData.voiceTone,
    personality: formData.personality,
    systemPrompt: formData.systemPrompt,
    firstMessage:
      formData.firstMessage || "Hello! How can I help you today?",
    temperature: formData.temperature ?? 0.5,
    knowledgeBaseFolderPath: formData.knowledgeBaseFolderPath,
    salesAgentEmails: (formData.salesAgentEmails || []).filter(
      (e: string) => e.trim() !== ""
    ),
    voiceProvider: activeVoiceProvider,

    // Behaviour toggles and their settings.
    //
    // Flow agents spread ...formData above and so carried these all along, but the
    // incoming branch builds an explicit allowlist and simply omitted them. Every
    // one of these was silently dropped on create: ticking Enable Meeting Booking,
    // Call Transfer, Language Detection or End Conversation had no effect
    // whatsoever, and the agent came back with all four off and no explanation.
    // The timezone went the same way, which matters more now that it is required.
    transferEnabled: formData.transferEnabled ?? false,
    transferPhoneNumber: formData.transferPhoneNumber || null,
    transferMessage: formData.transferMessage || null,
    detectLanguageEnabled: formData.detectLanguageEnabled ?? false,
    endConversationEnabled: formData.endConversationEnabled ?? false,
    meetingBookingEnabled: formData.meetingBookingEnabled ?? false,
    meetingBookingUrl: formData.meetingBookingUrl || null,
    meetingHostEmail: formData.meetingHostEmail || null,
    meetingHostName: formData.meetingHostName || null,
    timezone: formData.timezone || null,
  };

  // Provider-specific fields (Wizard parity)
  if (activeVoiceProvider === "bulbul") {
    payload.bulbulVoice = formData.bulbulVoice;
    payload.bulbulLanguage = formData.bulbulLanguage || formData.language;
  } else if (activeVoiceProvider === "nova_sonic") {
    // Nova Sonic (AWS HD Voices)
    payload.novaSonicVoice = formData.novaSonicVoice;
  } else if (activeVoiceProvider === "gemini") {
    // Google Gemini Live
    payload.geminiVoice = formData.geminiVoice;
  } else if (activeVoiceProvider === "kie-elevenlabs") {
    // Kie.ai ElevenLabs (Indian orgs)
    payload.kieVoice = formData.kieVoice;
  } else {
    // ElevenLabs ConvAI
    payload.elevenLabsVoiceId = formData.elevenLabsVoiceId;
    payload.voiceStability = formData.voiceStability;
    payload.voiceSimilarityBoost = formData.voiceSimilarityBoost;
    payload.voiceSpeed = formData.voiceSpeed;
  }

  // Only include type on create (Wizard behavior)
  if (!isEdit) {
    payload.type = "incoming";
  }

  return payload;
}
